<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary" 
              v-bind="attrs"
              v-on="on">
              Add Widget
            </v-btn>
          </template>
          <v-list flat>
            <v-list-item @click="onAdd(widget)" v-for="widget in Object.values(widgets)" :key="widget.name">
              <v-icon class="mr-2" v-text="icons.mdiWidgets"></v-icon>
              <v-list-item-content>{{widget.name}}</v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn class="ml-3" v-if="noWidget" @click="initDefaultWidgets()">Initialize Default Widgets</v-btn>
      </v-col>
      
      <v-row class="d-flex flex-row mb-4" v-for="(grouped, i) in groupedChildren" :key="i">
        <v-col
          cols="12"
          :md="wjson.type.md || 4"
          v-for="[widget, wjson] in grouped"
          :key="widget.uuid"
        >
          <v-menu bottom left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                absolute
                right
                text
                v-bind="attrs"
                v-on="on"
              >
                <v-icon color="grey lighten-1">{{icons.mdiDotsVertical}}</v-icon>
              </v-btn>
            </template>
            <v-list flat>
              <v-list-item @click="onConfig(widget.uuid)">
                <v-icon class="mr-2" v-text="icons.mdiCog"></v-icon>
                <v-list-item-content>Configure</v-list-item-content>
              </v-list-item>
              <v-list-item @click="onRemove(widget.uuid)">
                <v-icon class="mr-2" v-text="icons.mdiDelete"></v-icon>
                <v-list-item-content>Remove</v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-card>
            <component
              :is="wjson.type"
              :showConfigDialog="isShowConfig(widget.uuid)"
              :id="widget.uuid"
              :config="widget.config"
              @onConfigureClose="onConfigureClose"
              @onNewConfig="onNewConfig"
            ></component>
          </v-card>
        </v-col>
      </v-row>
    </v-row>
  </v-container>
</template>

<script>
/* eslint-disable vue/no-unused-components */
/* eslint-disable no-unused-vars */
import { mdiWidgets, mdiCog, mdiDelete, mdiDotsVertical } from "@mdi/js";
import { createGrouping, DashboardWidget, uuid, Account } from "@/model";
import AccountDatesConfig from "./AccountDatesConfig.js";
import Outstanding from "./OutstandingBalance.vue";
import MonthlyEvolution from "./MonthlyEvolution.vue";

export default {
  name: "Dashboard",
  components: {
    Outstanding,
    MonthlyEvolution,
  },
  async created() {
    await this.$store.dispatch("getDashboardWidgets");
  },
  data: () => ({
    icons: { mdiWidgets, mdiCog, mdiDotsVertical, mdiDelete },
    componentConfig: {},
    widgets: {"Outstanding" : {name: "Outstanding", type: Outstanding, typeOrder: 1}, 
              "MonthlyEvolution" : {name: "MonthlyEvolution", type: MonthlyEvolution, typeOrder: 2},
              }
  }),
  computed: {
    noWidget() {
      return this.$store.state.dashboardWidgets.length === 0;
    },
    children() {
      const dashboardWidgets = this.$store.state.dashboardWidgets;
      const widgets = this.widgets;
      const ws = dashboardWidgets
        .map(widget => {
          if (widgets[widget.componentName]) {
            const wjson = widgets[widget.componentName];
            const typeOrder = wjson.typeOrder;
            const widgetOrder = (typeOrder * 100000) + parseInt(widget.config.order);
            return [widget, wjson, widgetOrder];
          }
          return undefined;
        })
        .filter(tup => tup !== undefined)
        .sort((t1, t2) => {
          if (t1[2] > t2[2]) {
            return 1;
          }
          return -1;
        });
      return ws;
    },
    groupedChildren() {
      if (!this.children || this.children.length === 0) {
        return [];
      }
      const grouped = createGrouping(this.children, tup => tup[1].name)
      
      return Object.entries(grouped)
        .sort((e1, e2) => {
          const wjson1 = this.widgets[e1[0]];
          const wjson2 = this.widgets[e2[0]];
          if (wjson1.typeOrder > wjson2.typeOrder) {
            return 1;
          }
          return -1;
        })
        .map(ent => ent[1]);
    },
    maxOrder() {
      return this.children.map(([widget]) => parseInt(widget.config.order)).reduce((a,b) => (a > b) ? a : b, 0);
    }
  },
  methods: {
    async onAdd(widget) {
      const id = uuid();
      const nextOrder = this.maxOrder + 1;
      const config = {order: JSON.stringify(nextOrder)};
      const dashboardWidget = new DashboardWidget(id, widget.name, config);
      await this.$store.dispatch("saveDashboardWidget", dashboardWidget);
    },
    isShowConfig(uuid) {
      if (uuid in this.componentConfig) {
        return this.componentConfig[uuid];
      }
      return false;
    },
    onConfig(uuid) {
      this.$set(this.componentConfig, uuid, true);
    },
    onConfigureClose(uuid) { 
      setTimeout(() => this.$set(this.componentConfig, uuid, false), 0);
    },
    async onRemove(childUuid) {
      const tuple = this.children.find(([widget]) => widget.uuid === childUuid);
      if (!tuple) {
        return;
      }
      await this.$store.dispatch("deleteDashboardWidget", tuple[0]);
    },
    async onNewConfig(childUuid, configJson) {
      const widgetTuple = this.children.find(([widget]) => widget.uuid === childUuid);
      if (!widgetTuple) {
        return;
      }
      const widget = widgetTuple[0];
      const newConfig = Object.assign(widget.config, configJson);
      const dashboardWidget = widget.builder()
        .config(newConfig)
        .build();
      await this.$store.dispatch("editDashboardWidget", dashboardWidget);
    },
    async initDefaultWidgets() {
      await this.$store.dispatch("getAccounts");
      const accounts = this.$store.state.accounts;
      if (!accounts || accounts.length === 0) {
        return;
      }
      const [_, roots] = Account.createHierarchyTree(accounts);

      let order = this.maxOrder + 1;
      const defaultWidgets = [];
      for (const account of roots) {
        const widgetConfig = new AccountDatesConfig({account: account.uuid, displayName: account.name});
        const outstanding = new DashboardWidget(uuid(), "Outstanding", Object.assign({"order": order.toString()}, widgetConfig.toJson()));
        order++;
        const monthlyEvolution = new DashboardWidget(uuid(), "MonthlyEvolution", Object.assign({"order": order.toString()}, widgetConfig.toJson()));
        order++;
        defaultWidgets.push(outstanding);
        defaultWidgets.push(monthlyEvolution);
      }
      await this.$store.dispatch("saveDashboardWidgets", defaultWidgets);
    }
  },
};
</script>
